import { LAUNCH_PARAMS_STORE_TOKEN, LaunchParamsStore } from '@cgs/machines';
import { INumberFormatter, NUMBER_FORMATTER_TOKEN } from '@cgs/common';
import {
  Compatibility,
  Container,
  SceneObject,
  TextSceneObject,
  TtfTextSceneObject,
  Vector2,
} from '@cgs/wge';

import { TrifectaGameRules } from '@cgs/provider-trifecta';

export class CorsicaGameRules extends TrifectaGameRules {
  private readonly launchParamsStore: LaunchParamsStore;
  private readonly numberFormatter: INumberFormatter;

  constructor(container: Container) {
    super(container);

    this.launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    this.numberFormatter = container.resolve<INumberFormatter>(NUMBER_FORMATTER_TOKEN);
  }

  public show(): void {
    this.rerenderPayTable();
    super.show();
  }

  private rerenderPayTable(): void {
    const symbols = this.launchParamsStore.state.symbols.filter((x) =>
      (x.gains || []).some((gain) => gain)
    );

    const markerText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>('price_3_3');

    const startFontSize = markerText?.forceFontSize ?? markerText?.fontSize ?? 17;

    let minFontSize = startFontSize;

    symbols.forEach((symbol) => {
      symbol.gains!.forEach((gain, index) => {
        if (!gain) {
          return;
        }
        const symbolText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
          `price_${symbol.id}_${index + 1}`
        );
        if (!symbolText) {
          return;
        }

        symbolText.text = `${index + 1}x ${this.currency} ${this.numberFormatter.format(
          gain * this.slotSession.currentBet.bet,
          this.currencyDecimals
        )}`;

        const fontCoefficient = 13.5 / symbolText.text.length;

        minFontSize = Math.min(minFontSize, startFontSize * fontCoefficient);
      });
    });

    symbols.forEach((symbol) => {
      symbol.gains!.forEach((gain, index) => {
        if (!gain) {
          return;
        }
        const symbolText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
          `price_${symbol.id}_${index + 1}`
        );
        if (!symbolText) {
          return;
        }
        symbolText.fontSize = minFontSize;
      });
    });
  }

  private increaseMobileFontSize(): void {
    const markerText = this.scrollableProgressBar.scene.findById<TextSceneObject>('price_3_3');
    if (!markerText) {
      throw new Error('marker text not found');
    }

    markerText.parent!.childs.forEach((child) => {
      if (child instanceof TtfTextSceneObject) {
        const fontSize = this.localizationManager.getFontSize(child.id) ?? child.fontSize;

        child.forceFontSize = fontSize + 5;
      }
    });
  }

  private switchDescriptions(gameRulesScene: SceneObject) {
    const maskedGuerrillaDescription =
      gameRulesScene.findById<TtfTextSceneObject>('icon19DescriptionLbl_0')!;
    const policeDescription =
      gameRulesScene.findById<TtfTextSceneObject>('icon20DescriptionLbl\r')!;

    maskedGuerrillaDescription.id = 'icon20DescriptionLbl';
    policeDescription.id = 'icon19DescriptionLbl_0';
  }

  private renderGameSpecifications(): void {
    const gameRTP = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_0'
    );
    if (gameRTP) {
      gameRTP.text = gameRTP.text.replace('XX', `${this.launchParams.rtp}%`);
    }

    const buyBonusRTP = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_1'
    );
    if (buyBonusRTP) {
      buyBonusRTP.text = buyBonusRTP.text.replace('XX', `${this.launchParams.buyBonusRtp}%`);
    }

    const hitRateText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_1'
    );
    if (!hitRateText) {
      throw new Error('buy bonus RTP not found');
    }
    hitRateText.text = hitRateText.text.replace('XX', `${this.launchParams.hitRate}%`);

    const volatilityText = this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_2'
    );
    if (!volatilityText) {
      throw new Error('volatility not found');
    }
    volatilityText.text = volatilityText.text.replace('XX', this.launchParams.volatility);
  }

  private removeBuyFeatureDescription(gameRulesScene: SceneObject): void {
    const buyFeatureDescription = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureTitleLbl');

    const line1 = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_0');
    const line2 = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_1');
    const line3 = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_2');
    const line4 = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_3');
    const line5 = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_4');

    buyFeatureDescription?.parent!.removeChild(buyFeatureDescription);

    line1?.parent!.removeChild(line1);
    line2?.parent!.removeChild(line2);
    line3?.parent!.removeChild(line3);
    line4?.parent!.removeChild(line4);
    line5?.parent!.removeChild(line5);
  }

  private removeRTPDescription(gameRulesScene: SceneObject): void {
    const rtpDescription = gameRulesScene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_0'
    )!;

    const buyFeatureRtp = gameRulesScene.findById<TtfTextSceneObject>('buyFeatureDescriptionLbl_1');

    rtpDescription?.parent!.removeChild(rtpDescription);
    buyFeatureRtp?.parent!.removeChild(buyFeatureRtp);
  }

  private removeMaxWinDescription(gameRulesScene: SceneObject): void {
    const maxWinDescription = gameRulesScene.findById<TtfTextSceneObject>(
      'gameSpecificationsDescriptionLbl_3'
    );
    const maxWinDescription2 = gameRulesScene.findById<TtfTextSceneObject>(
      'symbolsAndPayoutsDescriptionLbl_3'
    );
    const maxWinDescription3 = gameRulesScene.findById<TtfTextSceneObject>(
      'buyFeatureDescriptionLbl_4'
    );

    maxWinDescription?.parent!.removeChild(maxWinDescription);
    maxWinDescription2?.parent!.removeChild(maxWinDescription2);
    maxWinDescription3?.parent!.removeChild(maxWinDescription3);
  }

  private removeExtraLines(gameRulesScene: SceneObject): void {
    const extraLine = gameRulesScene.findById<TtfTextSceneObject>('introductionDescriptionLbl_3');
    const extraLine2 = gameRulesScene.findById<TtfTextSceneObject>('introductionDescriptionLbl_4');
    extraLine?.parent!.removeChild(extraLine);
    extraLine2?.parent!.removeChild(extraLine2);
  }

  private alignDotsInBonusSection() {
    const descriptionsIds = [
      'freeSpinsRoundDescriptionLbl_2_0',
      'freeSpinsRoundDescriptionLbl_2_1',
      'freeSpinsRoundDescriptionLbl_1_0',
      'freeSpinsRoundDescriptionLbl_1_1',
      'freeSpinsRoundDescriptionLbl_1_2',
      'freeSpinsRoundDescriptionLbl_0_0',
      'freeSpinsRoundDescriptionLbl_0_1',
      'freeSpinsRoundDescriptionLbl_0_2',
      'freeSpinsRoundDescriptionLbl_0_3',
      'freeSpinsRoundDescriptionLbl_0_4',
    ];
    const hideDots = ['freeSpinsRoundDescriptionLbl_0_1', 'freeSpinsRoundDescriptionLbl_0_3'];

    descriptionsIds.forEach((descriptionId) => {
      const [, index, subIndex] = descriptionId.split('_');
      const dotId = `bullet_${index}_${subIndex}`;

      const description =
        this.scrollableProgressBar.scene.findById<TtfTextSceneObject>(descriptionId);
      const dot = this.scrollableProgressBar.scene.findById<SceneObject>(dotId);

      const isDotHidden = hideDots.includes(descriptionId);

      if (isDotHidden) {
        dot?.parent!.removeChild(dot);
        return;
      }

      if (description && dot) {
        dot.position = new Vector2(dot.position.x, description.position.y).add(
          new Vector2(0, description.fontSize * 0.15)
        );
      }
    });
  }

  protected getMarkerTextId(): string {
    return 'price_3_3';
  }

  protected afterInitGameRules(): void {
    this.renderGameSpecifications();
    this.alignDotsInBonusSection();
  }

  /**
   * Workaround for a WGE quirk: SceneObject.initialize() iterates the raw childs
   * array by index, while the `childs` getter z-sorts that same array in place.
   * When LocalizationManager applies AutoHeightDecorator during init (autoHeight
   * texts in localization.default.json), its `parent.childs` access re-sorts the
   * array mid-loop, later siblings get skipped, their `initialized` event never
   * fires and localization texts stay empty. Touching `childs` on every node
   * beforehand flushes the pending sort, so nothing reorders during initialize().
   */
  private flushPendingChildSort(scene: SceneObject): void {
    scene.traverseAll((node) => void node.childs);
  }

  protected beforeInitGameRules(gameRulesScene: SceneObject): void {
    this.flushPendingChildSort(gameRulesScene);
    this.flushPendingChildSort(this.gameRulesPopup);

    if (Compatibility.IsMobileBrowser) {
      this.increaseMobileFontSize();
    }
    this.removeExtraLines(gameRulesScene);

    this.switchDescriptions(gameRulesScene);

    if (!this.launchParams.showBuyFeatures) {
      this.removeBuyFeatureDescription(gameRulesScene);
    }

    if (!this.launchParams.showRtp) {
      this.removeRTPDescription(gameRulesScene);
    }

    if (!this.launchParams.showMaxWin) {
      this.removeMaxWinDescription(gameRulesScene);
    }
  }
}
